import { openLoginRewardModal } from '~/modals/login'

/**
 * Sliding 24 h session (see App\Service\DailyVisit on the API):
 * - GET /secure/user sends a new token once the current one is an hour old: stored in place of the old one, on the
 *   server render too (the cookie goes with the page), so a Waper who comes back every day is never logged out;
 * - the first visit of the day of a session still open earns the daily login point: its popin comes with the session
 *   loaded at the page load, not after a login (the login flows open it themselves, useManagePostLogin).
 */
export default defineNuxtPlugin((nuxtApp) => {
  const auth = useAuth()
  const { setToken, data: sessionData } = useAuthState()

  const renew = (data: unknown) => {
    const token = (data as { token?: unknown } | null | undefined)?.token
    if (typeof token === 'string' && token !== '') {
      setToken(token)
    }
  }
  renew(auth.data.value)
  watch(() => auth.data.value, renew)

  // session left out of the page: a page served from the Cloudflare cache was rendered for a visitor (its payload
  // carries no session and a null token), while the browser still holds the token cookie, for instance with a cookie
  // set before it became lax, or a cache rule that missed it. The user is loaded once the page is hydrated (before,
  // the logged-in header would differ from the server HTML). An API refusal (401/403) ends the stale session; any
  // other error keeps the cookie for the next page.
  if (import.meta.client) {
    const tokenCookie = useCookie<string | null>('auth.token')
    nuxtApp.hook('app:suspense:resolve', async () => {
      const token = tokenCookie.value
      if (auth.data.value || !token) {
        return
      }
      try {
        const user = await getUser(token)
        setToken(token)
        sessionData.value = user
      } catch (error) {
        const status = (error as { statusCode?: number })?.statusCode
        if (status === 401 || status === 403) {
          tokenCookie.value = null
        }
      }
    })
  }

  // opened once the page is hydrated: the ModalsContainer lives in the layout, inside the page Suspense, and a
  // popin rendered while that Suspense still hydrates (app:mounted) differs from the server HTML, so Vue drops the
  // whole page (grey overlay over a blank page, still blank once the popin is closed)
  if (import.meta.client) {
    let pending = true
    nuxtApp.hook('app:suspense:resolve', () => {
      if (!pending) {
        return
      }
      pending = false
      const reward = streakRewardData(auth.data.value)
      if (reward) {
        openLoginRewardModal(reward)
      }
    })
  }
})
